import { useCallback, useEffect, useRef, useState } from 'react';
import type { Friend, Group, Identity, Message, Subject } from './types';
import { getDb } from './lib/db';
import {
  deleteEnvelopes,
  fetchMyEnvelopes,
  fetchUserDevices,
  lookupByUsername,
  touchDeviceLastSeen,
} from './lib/relay';
import {
  ensureDeviceRegistered,
  fetchOwnIdentityRow,
  publishIdentityForUser,
  signOut,
} from './lib/identity';
import {
  clearSignalStoreCache,
  replenishOneTimePrekeysIfLow,
  unsealAndDecrypt,
} from './lib/signal';
import {
  clearAll,
  loadFriends,
  loadGroupMessages,
  loadGroups,
  loadIdentity,
  loadMessages,
  saveFriends,
  saveGroupMessages,
  saveGroups,
  saveMessages,
} from './lib/store';
import { Register } from './pages/Register';
import { Home } from './pages/Home';
import { Conversation } from './pages/Conversation';
import { Inspector } from './pages/Inspector';
import { QRAdd } from './pages/QRAdd';
import { GroupCreate } from './pages/GroupCreate';
import { Settings } from './pages/Settings';

const POLL_INTERVAL_MS = 3000;

type Screen = 'home' | 'inspector' | 'qr' | 'group-create' | 'settings';
type Boot = 'loading' | 'ready';

export default function App() {
  const [boot, setBoot] = useState<Boot>('loading');
  const [me, setMe] = useState<Identity | null>(null);
  const [friends, setFriends] = useState<Friend[]>(() => loadFriends());
  const [groups, setGroups] = useState<Group[]>(() => loadGroups());
  const [screen, setScreen] = useState<Screen>('home');
  const [subject, setSubject] = useState<Subject | null>(null);
  const [messagesByFriend, setMessagesByFriend] = useState<Record<string, Message[]>>(() =>
    loadMessages(),
  );
  const [messagesByGroup, setMessagesByGroup] = useState<Record<string, Message[]>>(() =>
    loadGroupMessages(),
  );

  const friendsRef = useRef(friends);
  const groupsRef = useRef(groups);
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);
  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  const autoAddAttempted = useRef<Set<string>>(new Set());
  const pollingRef = useRef<number | null>(null);

  // ---- Back-gesture / back-button navigation ----
  // The app navigates with React state (subject + screen), not URLs, so by
  // default the OS back gesture has nothing to pop and exits/minimizes the
  // PWA. We integrate with the History API: when we open a "deep" view (a
  // conversation or a sub-screen), we push one history entry. The OS back
  // gesture (or our own back buttons, which call history.back()) pops that
  // entry, fires popstate, and we return to Home — without leaving the app.
  const isDeepView = !!me && (subject !== null || screen !== 'home');
  const deepRef = useRef(isDeepView);
  const prevDeepRef = useRef(isDeepView);

  useEffect(() => {
    deepRef.current = isDeepView;
    // Push a trap entry exactly when we transition Home -> deep.
    if (isDeepView && !prevDeepRef.current) {
      const st = window.history.state as { norushDeep?: boolean } | null;
      if (!st || !st.norushDeep) {
        window.history.pushState({ norushDeep: true }, '');
      }
    }
    prevDeepRef.current = isDeepView;
  }, [isDeepView]);

  useEffect(() => {
    function onPop() {
      // Back fired. If we were in a deep view, swallow it and go Home.
      // If we're already at Home, do nothing and let the browser exit —
      // that's the correct behavior at the root.
      if (deepRef.current) {
        setSubject(null);
        setScreen('home');
      }
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // All in-app "back to Home" controls route through this so the pushed
  // history entry is consumed in lockstep with the UI returning Home.
  const goHome = useCallback(() => {
    if (deepRef.current) {
      window.history.back();
    } else {
      setSubject(null);
      setScreen('home');
    }
  }, []);

  useEffect(() => {
    saveMessages(messagesByFriend);
  }, [messagesByFriend]);
  useEffect(() => {
    saveGroupMessages(messagesByGroup);
  }, [messagesByGroup]);

  const [resumeUserId, setResumeUserId] = useState<string | null>(null);
  const [resumeUsername, setResumeUsername] = useState<string | null>(null);

  // Boot: check for an existing Supabase auth session.
  //
  //  - Session exists + matching local identity → restore (`setMe`).
  //  - Session exists + no local identity + remote identity row exists →
  //    this is a returning user on a fresh device. Auto-publish this
  //    device under the existing username, no prompts. The phone number
  //    owns the username via auth.uid; renames belong in Settings, not
  //    in a register flow.
  //  - Session exists + no local identity + no remote identity row →
  //    this is a brand-new auth user who finished OTP but never picked
  //    a username (e.g. closed the tab mid-flow). Route them to the
  //    username step via Register.
  //  - No session → Register from phone step.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await getDb().auth.getSession();
        const session = data.session;
        if (!session) {
          if (!cancelled) setBoot('ready');
          return;
        }
        const localId = loadIdentity();
        if (localId && localId.id === session.user.id) {
          if (!cancelled) setMe(localId);
          return;
        }

        // Session exists but no matching local identity.
        const remote = await fetchOwnIdentityRow(session.user.id);
        if (remote) {
          // Returning user, fresh device. Auto-publish under existing name.
          try {
            const me = await publishIdentityForUser({
              userId: session.user.id,
              username: remote.username,
            });
            if (!cancelled) setMe(me);
          } catch (err) {
            console.error('[no-rush] auto device-publish failed:', err);
            if (!cancelled) {
              setResumeUserId(session.user.id);
              setResumeUsername(remote.username);
            }
          }
        } else {
          // Brand-new auth user who hasn't picked a username yet.
          if (!cancelled) setResumeUserId(session.user.id);
        }
      } catch (err) {
        console.error('boot error:', err);
      } finally {
        if (!cancelled) setBoot('ready');
      }
    })();

    const {
      data: { subscription },
    } = getDb().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && !cancelled) {
        setMe(null);
        setSubject(null);
        setScreen('home');
        setResumeUserId(null);
        setResumeUsername(null);
        autoAddAttempted.current.clear();
        clearSignalStoreCache();
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // When we have an identity (either restored at boot or fresh from
  // register), make sure THIS install's device row is on the relay.
  // ensureDeviceRegistered is idempotent — it confirms our device_id
  // still exists server-side, or republishes if it's missing (e.g. after
  // a server wipe like the one we did on 2026-05-28). After the device
  // row is confirmed, top up our one-time prekey pool so new senders can
  // always start a session with us. Then refresh the device lists of all
  // known friends + group members so sender fan-out has accurate routing.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (async () => {
      try {
        const updated = await ensureDeviceRegistered(me);
        if (cancelled) return;
        if (updated.device_id !== me.device_id) {
          setMe(updated);
        }
        await replenishOneTimePrekeysIfLow({
          userId: updated.id,
          deviceId: updated.device_id,
        });
        if (cancelled) return;
        await backfillFriendDevices();
        if (cancelled) return;
        await backfillGroupMemberDevices();
      } catch (err) {
        if (!cancelled) {
          console.error('[no-rush] device / opk / backfill failed:', err);
        }
      }
    })();

    async function backfillFriendDevices() {
      const stale = friendsRef.current.filter(
        (f) => !f.devices || f.devices.length === 0,
      );
      if (stale.length === 0) return;
      const fetched = await Promise.all(
        stale.map(async (f) => {
          try {
            const devices = await fetchUserDevices(f.id);
            return devices.length > 0 ? { id: f.id, devices } : null;
          } catch {
            return null;
          }
        }),
      );
      const updates = new Map(
        fetched
          .filter((r): r is { id: string; devices: Friend['devices'] } => r !== null)
          .map((r) => [r.id, r.devices]),
      );
      if (updates.size === 0 || cancelled) return;
      const next = friendsRef.current.map((f) =>
        updates.has(f.id) ? { ...f, devices: updates.get(f.id)! } : f,
      );
      friendsRef.current = next;
      setFriends(next);
      saveFriends(next);
    }

    async function backfillGroupMemberDevices() {
      const currentGroups = groupsRef.current;
      const missing = new Map<string, true>();
      for (const g of currentGroups) {
        for (const m of g.members) {
          if (!m.devices || m.devices.length === 0) missing.set(m.id, true);
        }
      }
      if (missing.size === 0) return;

      const fetched = new Map<string, Friend['devices']>();
      for (const id of missing.keys()) {
        if (cancelled) return;
        try {
          const devices = await fetchUserDevices(id);
          if (devices.length > 0) fetched.set(id, devices);
        } catch {
          // skip
        }
      }
      if (fetched.size === 0 || cancelled) return;

      const next = currentGroups.map((g) => ({
        ...g,
        members: g.members.map((m) =>
          fetched.has(m.id) ? { ...m, devices: fetched.get(m.id)! } : m,
        ),
      }));
      groupsRef.current = next;
      setGroups(next);
      saveGroups(next);
    }

    return () => {
      cancelled = true;
    };
  }, [me]);

  // Polling: fetch envelopes from my mailbox, decrypt, auto-add unknown
  // senders. Runs while we have an identity.
  useEffect(() => {
    if (!me) return;
    const meCurrent = me;

    let cancelled = false;

    async function pollOnce() {
      try {
        const rows = await fetchMyEnvelopes(meCurrent.device_id);
        // Touch last_seen at the same cadence so the linked-devices list
        // can show a fresh "active" timestamp for this device.
        void touchDeviceLastSeen(meCurrent.device_id).catch(() => undefined);
        if (cancelled || rows.length === 0) return;

        const newFriendMessages: Message[] = [];
        const newGroupMessages: Message[] = [];
        const idsToDelete: string[] = [];
        const unknownSenders = new Set<string>();
        const seenGroups = new Map<string, string>();

        const reactionsToApply: Array<{
          group_id?: string;
          friend_username?: string;
          to_message_id: string;
          from: string;
          emoji: string;
        }> = [];

        for (const row of rows) {
          const payload = await unsealAndDecrypt({
            me: meCurrent,
            ciphertext_b64: row.ciphertext_b64,
          });
          if (!payload) continue;

          // Reaction envelopes are not new messages — they update an
          // existing one. Track them separately and apply after the
          // friend/group message inserts (which use functional state
          // updates, so ordering within the same poll cycle is safe).
          if (payload.reaction) {
            reactionsToApply.push({
              group_id: payload.group?.id,
              friend_username: payload.group ? undefined : payload.sender_username,
              to_message_id: payload.reaction.to_message_id,
              from: payload.sender_username,
              emoji: payload.reaction.emoji,
            });
            idsToDelete.push(row.id);
            continue;
          }

          const messageId = payload.message_id ?? row.id;

          if (payload.group) {
            newGroupMessages.push({
              id: messageId,
              group_id: payload.group.id,
              direction: 'in',
              body: payload.body,
              sent_at: payload.sent_at,
              group_sender: payload.sender_username,
              media: payload.media,
              kind: payload.kind,
              title: payload.title,
              letter_theme: payload.letter_theme,
              letter_font: payload.letter_font,
              letter_size: payload.letter_size,
              letter_signature: payload.letter_signature,
              reply_to: payload.reply_to,
            });
            seenGroups.set(payload.group.id, payload.group.name);
          } else {
            newFriendMessages.push({
              id: messageId,
              friend_username: payload.sender_username,
              direction: 'in',
              body: payload.body,
              sent_at: payload.sent_at,
              media: payload.media,
              kind: payload.kind,
              title: payload.title,
              letter_theme: payload.letter_theme,
              letter_font: payload.letter_font,
              letter_size: payload.letter_size,
              letter_signature: payload.letter_signature,
              reply_to: payload.reply_to,
            });
          }
          idsToDelete.push(row.id);

          const known = friendsRef.current.some(
            (f) => f.username === payload.sender_username,
          );
          if (
            !payload.group &&
            !known &&
            !autoAddAttempted.current.has(payload.sender_username)
          ) {
            unknownSenders.add(payload.sender_username);
          }
        }

        if (idsToDelete.length > 0) {
          await deleteEnvelopes(idsToDelete);
        }

        if (newFriendMessages.length > 0 && !cancelled) {
          setMessagesByFriend((prev) => {
            const next = { ...prev };
            for (const m of newFriendMessages) {
              const key = m.friend_username!;
              const arr = next[key] ?? [];
              if (!arr.some((x) => x.id === m.id)) {
                next[key] = [...arr, m];
              }
            }
            return next;
          });
        }

        if (newGroupMessages.length > 0 && !cancelled) {
          setMessagesByGroup((prev) => {
            const next = { ...prev };
            for (const m of newGroupMessages) {
              const key = m.group_id!;
              const arr = next[key] ?? [];
              if (!arr.some((x) => x.id === m.id)) {
                next[key] = [...arr, m];
              }
            }
            return next;
          });

          for (const [groupId, groupName] of seenGroups) {
            const known = groupsRef.current.some((g) => g.id === groupId);
            if (!known) {
              const newGroup: Group = {
                id: groupId,
                name: groupName,
                members: [],
                created_at: new Date().toISOString(),
              };
              const next = [...groupsRef.current, newGroup];
              groupsRef.current = next;
              setGroups(next);
              saveGroups(next);
            }
          }
        }

        // Apply reactions after message inserts. Each reaction can target
        // a message in messagesByFriend OR messagesByGroup. We attempt
        // both; whichever has the target wins.
        if (reactionsToApply.length > 0 && !cancelled) {
          for (const r of reactionsToApply) {
            if (r.group_id) {
              setMessagesByGroup((prev) => {
                const arr = prev[r.group_id!];
                if (!arr) return prev;
                const idx = arr.findIndex((m) => m.id === r.to_message_id);
                if (idx < 0) return prev;
                const updated = [...arr];
                const m = updated[idx];
                updated[idx] = {
                  ...m,
                  reactions: { ...(m.reactions ?? {}), [r.from]: r.emoji },
                };
                return { ...prev, [r.group_id!]: updated };
              });
            } else if (r.friend_username) {
              setMessagesByFriend((prev) => {
                const arr = prev[r.friend_username!];
                if (!arr) return prev;
                const idx = arr.findIndex((m) => m.id === r.to_message_id);
                if (idx < 0) return prev;
                const updated = [...arr];
                const m = updated[idx];
                updated[idx] = {
                  ...m,
                  reactions: { ...(m.reactions ?? {}), [r.from]: r.emoji },
                };
                return { ...prev, [r.friend_username!]: updated };
              });
            }
          }
        }

        for (const username of unknownSenders) {
          autoAddAttempted.current.add(username);
          void resolveAndAddFriend(username);
        }
      } catch (err) {
        console.error('poll error:', err);
      }
    }

    async function resolveAndAddFriend(username: string) {
      try {
        const found = await lookupByUsername(username);
        if (!found || cancelled) return;
        if (friendsRef.current.some((f) => f.username === username)) return;
        const next = [...friendsRef.current, found];
        friendsRef.current = next;
        setFriends(next);
        saveFriends(next);
      } catch (err) {
        console.error(`auto-add lookup for ${username} failed:`, err);
      }
    }

    void pollOnce();
    pollingRef.current = window.setInterval(pollOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [me]);

  const handleRegistered = useCallback((newMe: Identity) => {
    setMe(newMe);
    setResumeUserId(null);
    setResumeUsername(null);
  }, []);

  const handleAddFriend = useCallback(
    (f: Friend) => {
      const next = [...friends, f];
      setFriends(next);
      saveFriends(next);
    },
    [friends],
  );

  const handleSent = useCallback((m: Message) => {
    if (m.friend_username) {
      const key = m.friend_username;
      setMessagesByFriend((prev) => {
        const arr = prev[key] ?? [];
        return { ...prev, [key]: [...arr, m] };
      });
    } else if (m.group_id) {
      const key = m.group_id;
      setMessagesByGroup((prev) => {
        const arr = prev[key] ?? [];
        return { ...prev, [key]: [...arr, m] };
      });
    }
  }, []);

  const handleReact = useCallback(
    (subject: Subject, message: Message, emoji: string) => {
      // Optimistically update local state so the user sees their own reaction
      // immediately without waiting for a poll cycle.
      if (!me) return;
      const username = me.username;
      const targetId = message.id;
      if (subject.kind === 'friend') {
        const key = subject.friend.username;
        setMessagesByFriend((prev) => {
          const arr = prev[key];
          if (!arr) return prev;
          const idx = arr.findIndex((m) => m.id === targetId);
          if (idx < 0) return prev;
          const updated = [...arr];
          const m = updated[idx];
          updated[idx] = {
            ...m,
            reactions: { ...(m.reactions ?? {}), [username]: emoji },
          };
          return { ...prev, [key]: updated };
        });
      } else {
        const key = subject.group.id;
        setMessagesByGroup((prev) => {
          const arr = prev[key];
          if (!arr) return prev;
          const idx = arr.findIndex((m) => m.id === targetId);
          if (idx < 0) return prev;
          const updated = [...arr];
          const m = updated[idx];
          updated[idx] = {
            ...m,
            reactions: { ...(m.reactions ?? {}), [username]: emoji },
          };
          return { ...prev, [key]: updated };
        });
      }
    },
    [me],
  );

  const handleCreateGroup = useCallback(
    (group: Group) => {
      const next = [...groups, group];
      setGroups(next);
      saveGroups(next);
      setScreen('home');
      setSubject({ kind: 'group', group });
    },
    [groups],
  );

  const handleLogout = useCallback(async () => {
    clearAll();
    await signOut().catch(() => {});
    setMe(null);
    setFriends([]);
    setGroups([]);
    setSubject(null);
    setMessagesByFriend({});
    setMessagesByGroup({});
    setScreen('home');
    autoAddAttempted.current.clear();
  }, []);

  if (boot === 'loading') {
    return (
      <div className="screen screen-center">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!me) {
    return (
      <Register
        onRegistered={handleRegistered}
        resumeUserId={resumeUserId}
        resumeUsername={resumeUsername}
      />
    );
  }

  if (subject) {
    const messages =
      subject.kind === 'friend'
        ? messagesByFriend[subject.friend.username] ?? []
        : messagesByGroup[subject.group.id] ?? [];
    return (
      <Conversation
        me={me}
        subject={subject}
        messages={messages}
        onBack={goHome}
        onSent={handleSent}
        onReact={(m, emoji) => handleReact(subject, m, emoji)}
      />
    );
  }

  if (screen === 'inspector') {
    return <Inspector me={me} onBack={goHome} />;
  }

  if (screen === 'settings') {
    return (
      <Settings
        me={me}
        onBack={goHome}
        onSignedOut={handleLogout}
        onRenamed={(updated) => setMe(updated)}
      />
    );
  }

  if (screen === 'qr') {
    return (
      <QRAdd
        me={me}
        friends={friends}
        groups={groups}
        onAddFriend={handleAddFriend}
        onAddGroup={(g) => {
          if (!groupsRef.current.some((existing) => existing.id === g.id)) {
            const next = [...groupsRef.current, g];
            groupsRef.current = next;
            setGroups(next);
            saveGroups(next);
          }
          goHome();
        }}
        onBack={goHome}
      />
    );
  }

  if (screen === 'group-create') {
    return (
      <GroupCreate
        friends={friends}
        onCreate={handleCreateGroup}
        onBack={goHome}
      />
    );
  }

  return (
    <Home
      me={me}
      friends={friends}
      groups={groups}
      messagesByFriend={messagesByFriend}
      messagesByGroup={messagesByGroup}
      onAddFriend={handleAddFriend}
      onOpenFriendConversation={(f) => setSubject({ kind: 'friend', friend: f })}
      onOpenGroupConversation={(g) => setSubject({ kind: 'group', group: g })}
      onOpenInspector={() => setScreen('inspector')}
      onOpenQR={() => setScreen('qr')}
      onCreateGroup={() => setScreen('group-create')}
      onOpenSettings={() => setScreen('settings')}
      onLogout={handleLogout}
    />
  );
}
